import { createServerFn } from "@tanstack/react-start";
import { getRequestHeader } from "@tanstack/react-start/server";
import { z } from "zod";
import type { GapThread } from "./reddit-gap.server";

const ScanInput = z.object({
  domain: z.string().min(3).max(255),
  category: z.string().max(80).optional(),
  competitors: z.array(z.string().max(60)).max(3).optional(),
});

const EmailInput = z.object({
  id: z.string().uuid(),
  email: z.string().email().max(255),
});

export type RedditGapStatus =
  | "ok"
  | "invalid_domain"
  | "ip_limited"
  | "ceiling_reached"
  | "no_threads"
  | "error";

export type RedditGapResult = {
  status: RedditGapStatus;
  id?: string;
  domain?: string;
  brand?: string;
  category?: string;
  competitors?: string[];
  competitorSource?: "user" | "ai";
  buyerPrompt?: string;
  brandNamedInAnswer?: boolean;
  brandRank?: number | null;
  answerExcerpt?: string;
  threads?: GapThread[];
  scanned?: number;
  excludedClosed?: number;
  excludedBrandPresent?: number;
  cache_hit?: boolean;
  message?: string;
};

/** Number of threads shown in full before the email gate. */
export const REDDIT_GAP_FREE_THREADS = 2;

export const runRedditGapCheck = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) => ScanInput.parse(input))
  .handler(async ({ data }): Promise<RedditGapResult> => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { normalizeDomain, hashIp, localDay } = await import("./free-check.server");
    const { runRedditGapScan, REDDIT_GAP_CONFIG } = await import("./reddit-gap.server");

    const domain = normalizeDomain(data.domain);
    if (!domain) {
      return { status: "invalid_domain", message: "Enter a domain like yourcompany.com" };
    }

    const ip =
      getRequestHeader("cf-connecting-ip") ||
      getRequestHeader("x-forwarded-for")?.split(",")[0]?.trim() ||
      getRequestHeader("x-real-ip") ||
      null;
    // Same posture as /check: the IP cap, daily ceiling, and domain cache are
    // what actually bound the cost.

    const ipHash = hashIp(ip);
    const day = localDay();
    const { isAllowlistedIp } = await import("./ip-allowlist");
    const bypass = isAllowlistedIp(ip);

    const named = (data.competitors ?? []).map((c) => c.trim()).filter(Boolean);

    // 1) Domain cache. A visitor-supplied competitor list makes the scan
    //    specific to them, so it must never be served from another run.
    if (!bypass) {
      const cutoff = new Date(Date.now() - REDDIT_GAP_CONFIG.cacheDays * 86400_000).toISOString();
      const cached = await supabaseAdmin
        .from("reddit_gap_checks")
        .select("result, created_at")
        .eq("domain", domain)
        .eq("cache_hit", false)
        .eq("status", "ok")
        .gte("created_at", cutoff)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      const r: any = cached.data?.result;
      // Never serve a cached run when the visitor named their own competitors:
      // the whole point is that the threads are checked against those names.
      if (named.length === 0 && r?.threads?.length) {
        const ins = await supabaseAdmin
          .from("reddit_gap_checks")
          .insert({
            domain,
            ip_hash: ipHash,
            result: r,
            cache_hit: true,
            cost_usd: 0,
            status: "ok",
            local_day: day,
          })
          .select("id")
          .maybeSingle();
        return { status: "ok", id: ins.data?.id, domain, cache_hit: true, ...r };
      }

      // 2) Per-IP cap — shared across every public scan tool, plus a cooldown.
      const { checkPublicScanLimit } = await import("./public-scan-limit.server");
      const verdict = await checkPublicScanLimit(ipHash, day);
      if (!verdict.allowed) {
        return { status: "ip_limited", message: verdict.message };
      }

      // 3) Global daily ceiling.
      const dayCount = await supabaseAdmin
        .from("reddit_gap_checks")
        .select("id", { count: "exact", head: true })
        .eq("local_day", day)
        .eq("cache_hit", false)
        .eq("status", "ok");
      if ((dayCount.count ?? 0) >= REDDIT_GAP_CONFIG.dailyCeiling) {
        return {
          status: "ceiling_reached",
          message: "High demand right now — start your free trial to run this scan.",
        };
      }
    }

    const scan = await runRedditGapScan(domain, data.category, named);

    // Same inbound-lead treatment as a /check free check: prospect-queue
    // promotion + drip enrollment, so the follow-up emails are formulated
    // identically no matter which free tool the visitor used.
    let capture: unknown = null;
    if (scan.brand) {
      const { captureRedditGapLead } = await import("./reddit-gap-capture.server");
      capture = await captureRedditGapLead({
        domain,
        brand: scan.brand,
        category: scan.category,
      });
    }
    const promotion = (capture as { promotion?: unknown } | null)?.promotion ?? null;

    if (!scan.threads.length) {
      await supabaseAdmin.from("reddit_gap_checks").insert({
        domain,
        ip_hash: ipHash,
        cache_hit: false,
        cost_usd: scan.cost_usd,
        status: "no_threads",
        local_day: day,
        result: {
          brand: scan.brand,
          category: scan.category,
          competitors: scan.competitors,
          buyerPrompt: scan.buyerPrompt,
          brandNamedInAnswer: scan.brandNamedInAnswer,
          brandRank: scan.brandRank,
          answerExcerpt: scan.answerExcerpt,
          promotion,
          capture,
        } as unknown as never,
      });
      return {
        status: "no_threads",
        domain,
        brand: scan.brand,
        category: scan.category,
        competitors: scan.competitors,
        competitorSource: scan.competitorSource,
        buyerPrompt: scan.buyerPrompt,
        brandNamedInAnswer: scan.brandNamedInAnswer,
        brandRank: scan.brandRank,
        answerExcerpt: scan.answerExcerpt,
        scanned: scan.scanned,
        excludedClosed: scan.excludedClosed,
        excludedBrandPresent: scan.excludedBrandPresent,
        message:
          scan.excludedBrandPresent > 0
            ? "Every relevant thread we found already mentions you — no gap to close right now."
            : (named.length
              ? "We couldn't find open Reddit threads mentioning those competitors right now. Try different or better-known competitor names."
              : "We couldn't find open competitor threads for this category right now. Try naming 3 competitors."),
      };
    }

    const result = {
      brand: scan.brand,
      category: scan.category,
      competitors: scan.competitors,
      competitorSource: scan.competitorSource,
      buyerPrompt: scan.buyerPrompt,
      brandNamedInAnswer: scan.brandNamedInAnswer,
      brandRank: scan.brandRank,
      answerExcerpt: scan.answerExcerpt,
      threads: scan.threads,
      scanned: scan.scanned,
      excludedClosed: scan.excludedClosed,
      excludedBrandPresent: scan.excludedBrandPresent,
    };

    const ins = await supabaseAdmin
      .from("reddit_gap_checks")
      .insert({
        domain,
        ip_hash: ipHash,
        cache_hit: false,
        cost_usd: scan.cost_usd,
        status: "ok",
        local_day: day,
        result: { ...result, promotion, capture } as unknown as Record<string, unknown> as never,
      })
      .select("id")
      .maybeSingle();

    return { status: "ok", id: ins.data?.id, domain, cache_hit: false, ...result };

  });

/** Stores the email captured at the unlock gate against the scan row. */
export const submitRedditGapEmail = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) => EmailInput.parse(input))
  .handler(async ({ data }): Promise<{ ok: boolean }> => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { error } = await supabaseAdmin
      .from("reddit_gap_checks")
      .update({ email: data.email.toLowerCase() })
      .eq("id", data.id);
    return { ok: !error };
  });
